// The ClearMakes product catalog.
//
// This file is the single source of truth for every place products appear:
// the homepage catalog, /products, the header menu, the footer and the
// "Get started" product picker. To launch a new product, add one entry here
// (and a page for it under src/pages) — nothing else needs to change.

export type ProductStatus = "live" | "beta" | "coming-soon";

export interface Product {
  /** URL segment: /products/<slug> */
  slug: string;
  name: string;
  /** One line shown on cards and in menus. */
  tagline: string;
  /** Two or three sentences for the catalog card and meta description. */
  description: string;
  category: string;
  status: ProductStatus;
  /** Where "Get started" sends someone who picks this product. */
  startUrl: string;
  /** Where signed-in users open the product. */
  appUrl: string;
  pricingUrl?: string;
  /** Tools this product actually connects to (keys of `integrations`). */
  integrations: string[];
  /** Short capability list for cards. */
  highlights: string[];
  /**
   * A product that lives on its own site (agents.clearmakes.com). Menus and
   * cards link there instead of to /products/<slug>.
   */
  url?: string;
  /** What the drawn product preview on cards shows; Inbox's is the default. */
  preview?: ProductPreviewData;
}

export interface ProductPreviewData {
  nav: string[];
  /** `app` is an integration key for the row's logo; null shows none. */
  rows: { from: string; line: string; tag: string; app?: string | null }[];
  panel: { heading: string; body: string; subheading: string; box: string; primary: string; secondary: string };
}

export const products: Product[] = [
  {
    slug: "inbox",
    name: "ClearMakes Inbox",
    tagline: "One AI inbox for every channel your business uses.",
    description:
      "Bring Gmail, Outlook, Slack, Teams, Instagram and Messenger into one calm workspace. Inbox summarises what matters, drafts replies in your voice, and turns messages into meetings and workflows.",
    category: "Communication",
    status: "live",
    startUrl: "/login?view=signup",
    appUrl: "/platform",
    pricingUrl: "/pricing",
    integrations: ["gmail", "outlook", "slack", "teams", "instagram", "messenger", "google-calendar"],
    highlights: ["AI summaries", "Priority Hub", "Smart replies", "Calendar sync", "Workflows", "Smart FAQ"],
  },
  {
    slug: "agents",
    name: "ClearMakes Agents",
    tagline: "AI agents for your team and your website.",
    description:
      "Build AI agents from your documents, website and Google Drive. Connect Gmail, Slack, HubSpot and hundreds of apps or any MCP server, let them answer your team and do the busywork, and put the same agent on your website in one line.",
    category: "AI agents",
    status: "live",
    url: "https://agents.clearmakes.com",
    startUrl: "https://agents.clearmakes.com/signup",
    appUrl: "https://agents.clearmakes.com/admin",
    pricingUrl: "https://agents.clearmakes.com/pricing",
    integrations: ["gmail", "slack", "outlook", "teams", "google-calendar"],
    highlights: ["Team agents", "Website chatbot", "MCP servers", "Hundreds of apps", "Google Drive sync", "Answers with sources"],
    preview: {
      nav: ["Home", "Agents", "Conversations", "Knowledge", "Usage"],
      rows: [
        { from: "Support assistant", line: "Drafted 6 replies in Gmail", tag: "Done", app: "gmail" },
        { from: "Ops assistant", line: "Posted a summary to #support", tag: "Action", app: "slack" },
        { from: "Website chat", line: "Do you ship to Canada?", tag: "Answered", app: null },
        { from: "Sales copilot", line: "Booked a call for Thursday", tag: "Calendar", app: "google-calendar" },
        { from: "HR helpdesk", line: "Parental leave policy, p. 12", tag: "Source", app: null },
      ],
      panel: {
        heading: "Knowledge",
        body: "12 documents, acme.com (48 pages) and the Operations folder in Google Drive.",
        subheading: "Put it on your website",
        box: '<script src="agents.clearmakes.com/agents.js">',
        primary: "Copy",
        secondary: "Share link",
      },
    },
  },
];

export const liveProducts = products.filter((p) => p.status !== "coming-soon");

export const getProduct = (slug: string) => products.find((p) => p.slug === slug);

export const productPath = (p: Product) => p.url ?? `/products/${p.slug}`;
